import { ProcedureVersionModel } from "../api/APIClient";

/**
 * The calculation-procedure vocabulary, and the version -> draft conversion.
 *
 * These strings mirror `Capabilities/Assessment/Procedures/CalculationProcedureVocabulary.cs`. They
 * are plain string unions rather than generated enums because the backend serializes them as names.
 *
 * The labels matter more than they look. "AnywhereAssessmentGroupType" is meaningless to the person
 * authoring a procedure; "Any group of a type, anywhere below the anchor" is the thing they are
 * actually choosing.
 */

export const CALCULATION_METHODS = ["SimpleAverage", "WeightedAverage", "Aggregate"] as const;
export const MISSING_MARK_POLICIES = ["IgnoreMissing", "TreatMissingAsZero", "RequireAll"] as const;
export const TARGET_SELECTOR_KINDS = ["AssignedModule", "AssessmentGroupPath"] as const;
export const SOURCE_MODULE_KINDS = ["AssignedModule", "SpecificModule"] as const;
export const INPUT_SELECTOR_KINDS = [
    "AnchorResult",
    "AssessmentGroupPath",
    "AssessmentPath",
    "AnywhereAssessmentGroupType",
    "AnywhereAssessmentType",
    "ConcreteEntity",
] as const;
export const INPUT_ANCHORS = ["OutputTarget", "SourceModule"] as const;

export type CalculationMethod = (typeof CALCULATION_METHODS)[number];
export type MissingMarkPolicy = (typeof MISSING_MARK_POLICIES)[number];
export type TargetSelectorKind = (typeof TARGET_SELECTOR_KINDS)[number];
export type SourceModuleKind = (typeof SOURCE_MODULE_KINDS)[number];
export type InputSelectorKind = (typeof INPUT_SELECTOR_KINDS)[number];
export type InputAnchor = (typeof INPUT_ANCHORS)[number];

export const METHOD_LABELS: Record<CalculationMethod, string> = {
    SimpleAverage: "Simple average",
    WeightedAverage: "Weighted average",
    Aggregate: "Aggregate (totals preserved)",
};

/** What each method actually does to the numbers - worth saying, because the difference is not obvious. */
export const METHOD_HINTS: Record<CalculationMethod, string> = {
    SimpleAverage: "Each input counts equally. The result is normalised to a total of 100.",
    WeightedAverage: "Each input counts according to its weight. The result is normalised to a total of 100.",
    Aggregate: "Results are summed and so are their totals, so the marks stay raw rather than becoming a percentage.",
};

export const POLICY_LABELS: Record<MissingMarkPolicy, string> = {
    IgnoreMissing: "Ignore missing inputs",
    TreatMissingAsZero: "Treat missing inputs as zero",
    RequireAll: "Require every input",
};

export const POLICY_HINTS: Record<MissingMarkPolicy, string> = {
    IgnoreMissing: "A student with only some inputs is still given a mark, calculated from what exists.",
    TreatMissingAsZero: "A missing input counts as zero, which pulls the result down.",
    RequireAll: "No mark is produced at all unless every input is present.",
};

export const TARGET_SELECTOR_LABELS: Record<TargetSelectorKind, string> = {
    AssignedModule: "The qualification module the procedure is assigned to",
    AssessmentGroupPath: "A group at an exact path inside that module",
};

export const SOURCE_MODULE_LABELS: Record<SourceModuleKind, string> = {
    AssignedModule: "The assigned module",
    SpecificModule: "One named module",
};

export const INPUT_SELECTOR_LABELS: Record<InputSelectorKind, string> = {
    AnchorResult: "The anchor's own result",
    AssessmentGroupPath: "A group at an exact path",
    AssessmentPath: "Assessments of a type at an exact path",
    AnywhereAssessmentGroupType: "Any group of a type, anywhere below the anchor",
    AnywhereAssessmentType: "Any assessment of a type, anywhere below the anchor",
    ConcreteEntity: "One named group or assessment",
};

export const INPUT_ANCHOR_LABELS: Record<InputAnchor, string> = {
    OutputTarget: "The thing this rule produces a mark for",
    SourceModule: "The source module as a whole",
};

/** Which extra fields a rule's input selector needs, given its kind. */
export function ruleNeeds(kind: string) {
    return {
        groupPath: kind === "AssessmentGroupPath" || kind === "AssessmentPath",
        assessmentType: kind === "AssessmentPath" || kind === "AnywhereAssessmentType",
        groupType: kind === "AnywhereAssessmentGroupType",
        concrete: kind === "ConcreteEntity",
    };
}

// ------------------------------------------------------------------- draft

export interface RuleDraft {
    id?: string;
    componentMarkTypeID: string;
    weight?: number;
    sourceModuleKind: SourceModuleKind;
    sourceModuleID?: string;
    inputAnchor: InputAnchor;
    inputKind: InputSelectorKind;
    groupPath: string[];
    assessmentTypeID?: string;
    assessmentGroupTypeID?: string;
    concreteEntityType?: string;
    concreteEntityID?: string;
}

export interface ProcedureDraft {
    targetMarkTypeID: string;
    calculationMethod: CalculationMethod;
    missingMarkPolicy: MissingMarkPolicy;
    gradeScaleID?: string;
    targetSelectorKind: TargetSelectorKind;
    targetPath: string[];
    rules: RuleDraft[];
}

export const emptyDraft = (): ProcedureDraft => ({
    targetMarkTypeID: "",
    calculationMethod: "SimpleAverage",
    missingMarkPolicy: "IgnoreMissing",
    gradeScaleID: undefined,
    targetSelectorKind: "AssignedModule",
    targetPath: [],
    rules: [],
});

export const emptyRule = (): RuleDraft => ({
    componentMarkTypeID: "",
    weight: undefined,
    sourceModuleKind: "AssignedModule",
    inputAnchor: "OutputTarget",
    inputKind: "AnchorResult",
    groupPath: [],
});

/**
 * A published version is read back as resolved references (id + name) for display; editing it means
 * copying it into a draft of bare ids. This is the inverse of what the save endpoint expects.
 */
export function toDraft(version?: ProcedureVersionModel): ProcedureDraft {
    if (!version) return emptyDraft();

    return {
        targetMarkTypeID: version.targetMarkTypeID ?? "",
        calculationMethod: (version.calculationMethod as CalculationMethod) || "SimpleAverage",
        missingMarkPolicy: (version.missingMarkPolicy as MissingMarkPolicy) || "IgnoreMissing",
        gradeScaleID: version.gradeScaleID || undefined,
        targetSelectorKind: (version.targetSelectorKind as TargetSelectorKind) || "AssignedModule",
        targetPath: (version.targetPath ?? []).map((p) => p.id ?? ""),
        rules: (version.rules ?? []).map((rule: any) => ({
            id: rule.id,
            componentMarkTypeID: rule.componentMarkTypeID ?? "",
            weight: rule.weight ?? undefined,
            sourceModuleKind: (rule.sourceModuleSelectorKind as SourceModuleKind) || "AssignedModule",
            sourceModuleID: rule.sourceModule?.id,
            inputAnchor: (rule.inputAnchor as InputAnchor) || "OutputTarget",
            inputKind: (rule.inputSelectorKind as InputSelectorKind) || "AnchorResult",
            groupPath: (rule.groupPath ?? []).map((p: any) => p.id ?? ""),
            assessmentTypeID: rule.assessmentType?.id,
            assessmentGroupTypeID: rule.assessmentGroupType?.id,
            concreteEntityType: rule.concreteEntityType,
            concreteEntityID: rule.concreteEntityID,
        })),
    };
}
